import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  adoptAttachment,
  CASE_KEY,
  casePagePreviewUrl,
  correspondenceFileUrl,
  ProblemError,
  RECORD_NOT_FOUND,
  refusalCode,
  useCase,
  useCustomerProposal,
  type CaseDetailOut,
  type CustomerProposalOut,
  type DocumentOut,
  type ProposalAttachmentOut,
} from "@/api";
import { Bone } from "@/components/Bone";
import { Button } from "@/components/Button";
import { CancelledNotice } from "@/components/CancelledNotice";
import { CaseGone } from "@/components/CaseGone";
import { Checkbox } from "@/components/Checkbox";
import { FileIcon } from "@/components/icons";
import { ScreenHead } from "@/components/ScreenHead";
import { SketchViewer } from "@/components/SketchViewer";
import { StateMessage } from "@/components/StateMessage";
import { Tooltip } from "@/components/Tooltip";
import { clueLines, readFrom } from "@/features/customer/clues";
import { CustomerFromMail } from "@/features/customer/CustomerFromMail";
import { forgetArrival, isArrival } from "@/lib/eingangArrival";
import { clockTime, formatBytes, formatDay } from "@/lib/format";
import { outlineFor, recordOf } from "@/lib/noStand";
import { startReading } from "@/lib/startReading";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useDemoRun } from "@/store/demoRun";

import { drawingsOf, drawingType, onFile, tickedAtFirst, type DrawingRow } from "./drawings";
import styles from "./Eingang.module.css";
import { GratingTypeField } from "./GratingTypeField";

const TITLE = "Eingang";
const LOADING = "Eingang wird geladen";
/** AN-08 (Ruling R-18): said directly above the one button that transmits. */
const TRANSFER =
  "Die gewählten Zeichnungen werden für die Auslese an den Anbieter übertragen. Die Mail selbst bleibt hier.";
const NOTHING_TICKED = "Keine Zeichnung gewählt — ohne Zeichnung zeichnest du den Umriss von Hand.";

function messageOf(cause: unknown): string {
  if (cause instanceof ProblemError) return cause.userMessage;
  return cause instanceof Error && cause.message !== "" ? cause.message : "Der Server ist nicht erreichbar.";
}

/** An adoption that failed, with the file it was about — the sentence names it. */
class AdoptFailed extends Error {
  constructor(
    readonly filename: string,
    refusal: unknown,
  ) {
    super(messageOf(refusal));
  }
}

/**
 * `/vorgang/:caseId/eingang?mail=<entryId>` — where a customer's mail lands
 * after it was let go on the start page (spec 2026-10-20 §3.1, §9).
 *
 * Two columns from 1100 px, stacked below: the mail on the left, the work on
 * the right — who the customer is (`CustomerFromMail`), the contact person,
 * the grating type (with the mail's proposal, spec 2026-10-23 R-7), which
 * drawings are read, and the way on. Stacked, the work comes first:
 * the decision is what the screen is for, the mail is what it is checked
 * against. The work column is also first in the reading and Tab order, on
 * both widths.
 *
 * **Nothing leaves the house before „Zeichnung einlesen"** (AN-08): the
 * sentence that says so stands directly above that button. Pressed, each
 * ticked attachment is adopted — a copy, the only way from the
 * correspondence to the reading — and the reading starts as on the start
 * page, the page choice included; a stale demo run is thrown away first.
 * „Umriss zeichnen" is always there; „Kunde später" leaves for the record,
 * where the customer is asked for again (§3.4). No customer locks nothing.
 *
 * The URL is the state: a reload reads the Vorgang and the mail again and
 * shows what the server has — a customer assigned is the card, not the
 * choice. Without `?mail=` there is no Eingang: the record.
 */
export function EingangScreen() {
  const { caseId = "" } = useParams();
  const [params] = useSearchParams();
  const entryId = params.get("mail");
  const navigate = useNavigate();
  const detail = useCase(caseId || null);
  const proposal = useCustomerProposal(caseId, entryId);
  const gone = detail.error?.message === RECORD_NOT_FOUND;
  useDocumentTitle(gone ? null : TITLE, detail.data?.caseNo ?? null);

  if (caseId === "") return <Navigate to="/vorgaenge" replace />;
  if (entryId === null || entryId === "") return <Navigate to={recordOf(caseId)} replace />;
  if (gone) return <CaseGone caseId={caseId} onRegister={() => navigate("/vorgaenge")} />;
  if (detail.isPending || proposal.isPending) return <EingangSkeleton />;
  if (detail.data === undefined) {
    return (
      <div className={styles.screen}>
        <StateMessage
          title="Der Vorgang lässt sich gerade nicht laden."
          action={
            <Button variant="primary" busy={detail.isFetching} onClick={() => void detail.refetch()}>
              {detail.isFetching ? "Wird geladen …" : "Erneut versuchen"}
            </Button>
          }
        >
          Versuch es gleich noch einmal — an der Mail und am Vorgang ändert das nichts.
        </StateMessage>
      </div>
    );
  }
  return <Eingang key={`${caseId}:${entryId}`} caseId={caseId} entryId={entryId} detail={detail.data} proposal={proposal.data} />;
}

interface EingangProps {
  caseId: string;
  entryId: string;
  detail: CaseDetailOut;
  /** `undefined` where the mail could not be read out (404, 422, unreachable) — the block says why. */
  proposal: CustomerProposalOut | undefined;
}

/** The loaded screen — exported for the dev preview (`/_vorschau/eingang`), which has no route params. */
export function Eingang({ caseId, entryId, detail, proposal }: EingangProps) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const id = useId();
  const [ticks, setTicks] = useState<Record<string, boolean>>({});
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<{ title: string; text: string } | null>(null);
  /** A drawing on file shown large, in the app (Task 18). */
  const [looking, setLooking] = useState<DocumentOut | null>(null);
  const [customerHere, setCustomerHere] = useState(false);
  // Attachments adopted (or found on file) by a press that then failed
  // further on: the next press reads these documents instead of copying
  // again — which the server would refuse (409 `document_exists`).
  const adopted = useRef(new Map<string, DocumentOut>());

  const cancelled = detail.status === "cancelled";
  const { rows, others } = drawingsOf(proposal?.attachments ?? [], detail.documents ?? []);
  // Straight from the drop on the start page, or opened again (M-7) — read
  // at the first showing, spent once it stands. Opened again, what was on
  // the Vorgang then starts unticked; what a failed press copied since does
  // not count as on file before.
  const [arrival] = useState(() => isArrival(caseId, entryId));
  const [onFileAtOpen] = useState(() => new Set(rows.filter(onFile).map((row) => row.key)));
  const tickedOf = (row: DrawingRow) => ticks[row.key] ?? tickedAtFirst(row, arrival || !onFileAtOpen.has(row.key));

  // The screen opens on its heading (route focus, UX ruling 12) — after the
  // block below, whose form may have put the focus into itself.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  useEffect(() => {
    forgetArrival(caseId, entryId);
  }, [caseId, entryId]);

  const ticked = rows.filter(tickedOf);
  const canRead = ticked.length > 0;
  const hasCustomer = customerHere || (detail.customer ?? null) !== null;
  const nothingId = `${id}-nothing`;
  const transferId = `${id}-transfer`;

  /**
   * The document a refused copy already is (409 `document_exists`, Task 10
   * review M-5): the same bytes lie on the Vorgang under another name — a
   * drawing dropped beside the mail. Same bytes, same size and type: asked of
   * the Vorgang as it stands now, taken only where exactly one fits. Where
   * none does, the file is on the Vorgang all the same and the read goes on
   * without this row.
   */
  async function onFileAs(attachment: ProposalAttachmentOut): Promise<DocumentOut | null> {
    await client.refetchQueries({ queryKey: CASE_KEY(caseId), exact: true });
    const documents = client.getQueryData<CaseDetailOut>(CASE_KEY(caseId))?.documents ?? detail.documents ?? [];
    const fitting = documents.filter(
      (document) =>
        document.derivedFrom === null &&
        document.byteSize === attachment.byteSize &&
        document.mimeType === attachment.mimeType,
    );
    return fitting.length === 1 ? fitting[0]! : null;
  }

  async function read() {
    if (reading || !canRead) return;
    setReading(true);
    setReadError(null);
    try {
      const documents: DocumentOut[] = [];
      const take = (document: DocumentOut) => {
        if (!documents.some((taken) => taken.id === document.id)) documents.push(document);
      };
      for (const row of ticked) {
        if (row.kind === "document" || row.document !== null) {
          take(row.document!);
          continue;
        }
        let document: DocumentOut | null | undefined = adopted.current.get(row.attachment.id);
        if (document === undefined) {
          try {
            document = await adoptAttachment(caseId, entryId, row.attachment.id);
          } catch (cause) {
            if (refusalCode(cause) !== "document_exists") throw new AdoptFailed(row.attachment.filename, cause);
            document = await onFileAs(row.attachment);
          }
          if (document !== null) adopted.current.set(row.attachment.id, document);
        }
        if (document !== null) take(document);
      }
      if (documents.length === 0) throw new Error("Keine der gewählten Zeichnungen liegt lesbar im Vorgang.");
      // The Vorgang has new drawings: its documents, and with them the tabs of its frame.
      void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      // A reading of another Vorgang must not leak into this one's screens.
      useDemoRun.getState().reset();
      await startReading(caseId, documents);
      navigate(`/vorgang/${caseId}/einlesen`);
    } catch (cause) {
      // Copies made before the failure are documents of the Vorgang now —
      // its page and its tabs show them (Task 10 review, M-4).
      if (adopted.current.size > 0) void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      setReadError(
        cause instanceof AdoptFailed
          ? { title: `„${cause.filename}“ wurde nicht übernommen.`, text: cause.message }
          : { title: "Die Auslese ist nicht gestartet.", text: messageOf(cause) },
      );
      setReading(false);
    }
  }

  return (
    <div className={styles.screen}>
      {/* The Vorgang is named by the frame above this page, once (design
          2026-10-22 §5) — so the heading is the page's own name. */}
      <ScreenHead title={TITLE} headingRef={headingRef}>
        <p className={styles.lead}>Bestätige den Kunden und wähl die Zeichnungen, die gelesen werden.</p>
      </ScreenHead>

      {cancelled ? (
        <CancelledNotice
          cancellation={detail.cancellation ?? null}
          action={
            <Link className="inline-link" to={recordOf(caseId)}>
              Zur Akte
            </Link>
          }
        />
      ) : null}

      <div className={cancelled ? styles.single : styles.grid}>
        {cancelled ? null : (
          <div className={styles.work}>
            <div className={styles.panel}>
              <CustomerFromMail
                caseId={caseId}
                entryId={entryId}
                onDone={() => {
                  setCustomerHere(true);
                  // The customer stands: the way on is next.
                  nextRef.current?.focus();
                }}
              />
            </div>

            {/* What the Vorgang is planned and priced as — and what the mail
                asks for, as a proposal (spec 2026-10-23 R-7). Between the
                customer and the drawings: it is said in the same mail, and it
                is decided before the drawing is read. */}
            <GratingTypeField
              caseId={caseId}
              detail={detail}
              clue={proposal?.readable === true ? proposal.fields.gratingType : null}
            />

            <section className={styles.panel} aria-labelledby={`${id}-drawings`}>
              <h2 id={`${id}-drawings`} className={styles.panelTitle}>
                {/* The mesh marker before a panel's title, as `Panel` (design 2026-10-24 §8.6). */}
                <span className="marke" aria-hidden="true" />
                Zeichnungen aus der Mail
              </h2>
              {looking !== null ? (
                <SketchViewer caseId={caseId} document={looking} onClose={() => setLooking(null)} />
              ) : null}
              {rows.length > 0 ? (
                <ul className={styles.drawings}>
                  {rows.map((row) => (
                    <DrawingItem
                      key={row.key}
                      caseId={caseId}
                      row={row}
                      onLook={setLooking}
                      checked={tickedOf(row)}
                      disabled={reading}
                      onChange={(checked) => setTicks((current) => ({ ...current, [row.key]: checked }))}
                    />
                  ))}
                </ul>
              ) : proposal === undefined ? (
                // Not known — the mail could not be read out here; nothing is claimed about it.
                <p className={styles.quiet}>Die Anlagen der Mail lassen sich hier nicht zeigen — sie liegen in der Akte.</p>
              ) : (
                <p className={styles.quiet}>Die Mail bringt keine Zeichnung mit, die die Auslese lesen kann.</p>
              )}
              {others > 0 ? (
                <p className={styles.quiet}>
                  {others === 1
                    ? "Eine weitere Anlage kann die Auslese nicht lesen — sie liegt in der Akte."
                    : `${others} weitere Anlagen kann die Auslese nicht lesen — sie liegen in der Akte.`}
                </p>
              ) : null}
              {rows.length > 0 && !canRead ? (
                <p id={nothingId} className={styles.quiet}>
                  {NOTHING_TICKED}
                </p>
              ) : null}
            </section>

            <div className={styles.next}>
              {rows.length > 0 ? (
                <p id={transferId} className={styles.transfer}>
                  {TRANSFER}
                </p>
              ) : null}
              <div className={styles.nextActions}>
                {rows.length > 0 ? (
                  <Button
                    ref={canRead ? nextRef : undefined}
                    variant={canRead ? "primary" : "secondary"}
                    busy={reading}
                    aria-disabled={canRead ? undefined : "true"}
                    aria-describedby={canRead ? transferId : nothingId}
                    onClick={() => void read()}
                  >
                    {reading ? "Wird übernommen …" : "Zeichnung einlesen"}
                  </Button>
                ) : null}
                <Button
                  ref={canRead ? undefined : nextRef}
                  variant={canRead ? "secondary" : "primary"}
                  aria-disabled={reading ? "true" : undefined}
                  onClick={() => navigate(outlineFor(caseId))}
                >
                  Umriss zeichnen
                </Button>
                {hasCustomer ? null : (
                  <Tooltip text="Zur Akte, ohne Kunden — dort ordnest du ihn später zu.">
                    <Button
                      variant="quiet"
                      className={styles.later}
                      aria-disabled={reading ? "true" : undefined}
                      onClick={() => navigate(recordOf(caseId))}
                    >
                      Kunde später
                    </Button>
                  </Tooltip>
                )}
              </div>
              {readError !== null ? (
                <StateMessage tone="conflict" live="alert" title={readError.title}>
                  {readError.text}
                </StateMessage>
              ) : null}
            </div>
          </div>
        )}

        <MailView caseId={caseId} entryId={entryId} proposal={proposal} />
      </div>
    </div>
  );
}

interface DrawingItemProps {
  caseId: string;
  row: DrawingRow;
  /** A drawing on file, shown large in the app (Task 18). */
  onLook: (document: DocumentOut) => void;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}

/** One drawing with its tick: the file's name, its type and size, and why it is (not) ticked. */
function DrawingItem({ caseId, row, checked, disabled, onChange, onLook }: DrawingItemProps) {
  const metaId = useId();
  const name = row.kind === "attachment" ? row.attachment.filename : row.document.originalFilename;
  const mime = row.kind === "attachment" ? row.attachment.mimeType : row.document.mimeType;
  const size = row.kind === "attachment" ? row.attachment.byteSize : row.document.byteSize;
  // „schon im Vorgang", not „mit der Mail abgelegt" (Task 10 review, M-10):
  // a document does not say when or how it came, and one from an earlier
  // visit is no drawing of this mail.
  const note =
    row.kind === "document"
      ? "schon im Vorgang"
      : row.document !== null
        ? "schon im Vorgang"
        : row.attachment.drawingLikely
          ? null
          : "vermutlich ein Signaturbild";
  // On file, its first page small (Task 18): the drawing, not only its
  // name — and large in the app on a press. An attachment not copied yet has
  // no page the store can draw.
  const document = row.document;
  return (
    <li className={styles.drawing}>
      <span className={styles.drawingText}>
        <Checkbox checked={checked} disabled={disabled} describedBy={metaId} onChange={onChange}>
          <span className={styles.fileName}>{name}</span>
        </Checkbox>
        <span id={metaId} className={styles.fileMeta}>
          {[drawingType(mime), formatBytes(size), note].filter(Boolean).join(" · ")}
        </span>
      </span>
      {document !== null ? (
        <button
          type="button"
          className={styles.drawingThumb}
          aria-label={`${name} groß zeigen`}
          onClick={() => onLook(document)}
        >
          <DrawingThumb src={casePagePreviewUrl(caseId, document.id, 1)} />
        </button>
      ) : null}
    </li>
  );
}

/**
 * The first page small — or, where the store cannot draw it (gone, an
 * unreadable PDF), the file sign instead of the browser's broken picture
 * (review T18-1), as the record's own list does.
 */
function DrawingThumb({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  return failed ? (
    <FileIcon size={20} />
  ) : (
    // A page, and so a sheet: paper in the dark too (design 2026-10-24 §5.3).
    <img src={src} alt="" data-blatt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
  );
}

interface MailViewProps {
  caseId: string;
  entryId: string;
  proposal: CustomerProposalOut | undefined;
}

/**
 * The mail as it came: subject, sender, when, and its text — plain, with the
 * lines a value was read from marked, so a person sees where „aus der
 * Signatur" is. The text scrolls in its own box, never taller than the
 * screen; with the keyboard too (a focusable region).
 */
function MailView({ caseId, entryId, proposal }: MailViewProps) {
  const id = useId();
  const mail = proposal?.mail;
  const fields = proposal?.readable === true ? proposal.fields : null;
  const marked = fields !== null ? clueLines(fields) : new Set<string>();
  const fromMarked = fields !== null && readFrom(fields, "sender");
  const subjectMarked = fields !== null && readFrom(fields, "subject");
  const from = mail ? [mail.fromName, mail.fromAddr !== null ? `<${mail.fromAddr}>` : null].filter(Boolean).join(" ") : "";
  const body = mail?.bodyText ?? null;

  return (
    <section className={`${styles.panel} ${styles.mail}`} aria-labelledby={`${id}-mail`}>
      <div className={styles.mailHead}>
        <h2 id={`${id}-mail`} className={styles.panelTitle}>
          <span className="marke" aria-hidden="true" />
          Mail
        </h2>
        {proposal !== undefined ? (
          <a className="inline-link" href={correspondenceFileUrl(caseId, entryId)} download>
            Original herunterladen
          </a>
        ) : null}
      </div>
      {proposal === undefined ? (
        <p className={styles.quiet}>Die Mail lässt sich hier nicht zeigen.</p>
      ) : (
        <>
          <p className={styles.subject}>
            {mail?.subject ? subjectMarked ? <mark className={styles.hit}>{mail.subject}</mark> : mail.subject : "(ohne Betreff)"}
          </p>
          <dl className={styles.mailFacts}>
            {from !== "" ? (
              <div>
                <dt>Von</dt>
                <dd>{fromMarked ? <mark className={styles.hit}>{from}</mark> : from}</dd>
              </div>
            ) : null}
            {mail?.sentAt ? (
              <div>
                <dt>Gesendet</dt>
                <dd className="tabular">
                  {formatDay(mail.sentAt)}, {clockTime(mail.sentAt)} Uhr
                </dd>
              </div>
            ) : null}
          </dl>
          {body !== null && body.trim() !== "" ? (
            <div className={styles.body} role="region" aria-label="Text der Mail" tabIndex={0}>
              {body.split(/\r?\n/).map((line, index) => (
                <Fragment key={index}>
                  {index > 0 ? "\n" : null}
                  {marked.has(line.trimEnd()) ? <mark className={styles.hit}>{line}</mark> : line}
                </Fragment>
              ))}
            </div>
          ) : (
            <p className={styles.quiet}>
              {proposal.readable
                ? "Die Mail hat keinen Text."
                : "Den Text dieser Mail kann die Werkbank nicht zeigen — öffne das Original."}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** The screen's own geometry while the Vorgang and the mail load (UX ruling 5): nothing jumps. */
function EingangSkeleton() {
  return (
    <div className={styles.screen} role="region" aria-label={LOADING} aria-busy="true">
      <div className={styles.skeletonHead}>
        <Bone width="4rem" height="0.625rem" />
        <Bone width="20rem" height="1.25rem" />
      </div>
      <div className={styles.grid}>
        <div className={styles.work}>
          <div className={`${styles.panel} ${styles.skeletonPanel}`}>
            <Bone width="4rem" height="1rem" />
            <Bone width="100%" height="6rem" />
            <Bone width="100%" height="3.5rem" />
            <Bone width="60%" height="2.5rem" />
          </div>
          <div className={`${styles.panel} ${styles.skeletonPanel}`}>
            <Bone width="10rem" height="1rem" />
            <Bone width="70%" />
            <Bone width="55%" />
          </div>
        </div>
        <div className={`${styles.panel} ${styles.mail} ${styles.skeletonPanel}`}>
          <Bone width="3rem" height="1rem" />
          <Bone width="80%" />
          <Bone width="50%" />
          <Bone width="100%" height="12rem" />
        </div>
      </div>
    </div>
  );
}
